import { apolloClient } from "@/utils/apollo";
import type { IChatMessageWithLikes } from "../model/chat-model";
import { useUserStore } from "@/stores/user.store";
import {
  getFragmentData,
  graphql as gql,
  type DocumentType,
  type FragmentType,
} from "@/generated";
import { useTextModerationConfirmation } from "@/composables/global/use-text-moderation-confirmation";
import { ref } from "vue";
import type { IChatMessagePayload } from "@f3/shared/chat-types";

export const CHAT_MESSAGES_PAGE_SIZE = 100;

const ChatMessageForServiceFragment = gql(/* GraphQL */ `
  fragment ChatMessageForService on ChatMessageBase {
    id
    chat_room_id
    content
    created_at
    is_question
    is_pinned
    reply_to_id
    reply_to {
      id
      content
      created_by_id
      has_been_checked
      created_by {
        id
        username
        avatar
        country_iso
        user_channel_memberships {
          channel_membership {
            permission_mask
          }
        }
      }
      chat_message_translations {
        content
        language
      }
    }
    has_been_checked
    created_by_id
    created_by {
      id
      username
      avatar
      country_iso
      user_channel_memberships {
        channel_membership {
          permission_mask
        }
      }
    }
    chat_message_translations {
      content
      language
    }
    moderation_status
  }
`);

const ChatMessageWithLikesForServiceFragment = gql(/* GraphQL */ `
  fragment ChatMessageWithLikesForService on ChatMessageWithLikes {
    likes
    has_liked
    ...ChatMessageForService
  }
`);

const AddChatMessageDocument = gql(/* GraphQL */ `
  mutation addChatMessage(
    $chat_room_id: String!
    $content: String!
    $is_question: Boolean!
    $reply_to_id: String
    $moderation_confirmation_token: String!
  ) {
    addChatMessage(
      chat_room_id: $chat_room_id
      content: $content
      is_question: $is_question
      reply_to_id: $reply_to_id
      moderation_confirmation_token: $moderation_confirmation_token
    ) {
      ... on ChatMessage {
        ...ChatMessageForService
      }
    }
  }
`);

const LikeChatMessageDocument = gql(/* GraphQL */ `
  mutation LikeChatMessage($chat_message_id: String!, $liked: Boolean!) {
    likeChatMessage(chat_message_id: $chat_message_id, liked: $liked) {
      id
      user_id
      chat_message_id
    }
  }
`);

const ChatMessageDocument = gql(/* GraphQL */ `
  query ChatMessage($id: String!) {
    chatMessage(id: $id) {
      ...ChatMessageWithLikesForService
    }
  }
`);

const RoomChatMessagesDocument = gql(/* GraphQL */ `
  query RoomChatMessages(
    $chat_room_id: String!
    $limit: Int!
    $cursor: String
  ) {
    chatMessagesForRoom(
      chat_room_id: $chat_room_id
      limit: $limit
      cursor: $cursor
    ) {
      ...ChatMessageWithLikesForService
    }
  }
`);

const DeleteChatMessageDocument = gql(/* GraphQL */ `
  mutation DeleteChatMessage($id: String!) {
    deleteChatMessage(id: $id)
  }
`);

const SetMessagePinnedStateDocumment = gql(/* GraphQL */ `
  mutation SetMessagePinnedState($id: String!, $value: Boolean!) {
    setMessagePinnedState(value: $value, id: $id)
  }
`);

const ReportChatMessageDocument = gql(/* GraphQL */ `
  mutation ReportChatMessage($chat_message_id: String!, $reason: String) {
    reportChatMessage(chat_message_id: $chat_message_id, reason: $reason) {
      ... on ReportChatMessageSuccess {
        report_id
        chat_message_id
        status
      }
      ... on DuplicateReportError {
        error
      }
      ... on SelfReportError {
        error
      }
    }
  }
`);

export function mapPayloadToMessage(
  payload: IChatMessagePayload,
  userId: string | null,
  userLanguage: string,
): IChatMessageWithLikes {
  return {
    message: {
      id: payload.id,
      content: payload.content,
      chatRoomId: payload.chat_room_id,
      messageTranslation: payload.chat_message_translations.find(
        (t) => t.language === userLanguage,
      )?.content,
      isVisible: payload.has_been_checked || payload.created_by_id === userId,
      isVerified: payload.has_been_checked,
      isQuestion: payload.is_question,
      isPinned: payload.is_pinned,
      moderationStatus: payload.moderation_status,
      timestamp: payload.created_at,
      replyTo: payload.reply_to
        ? {
            id: payload.reply_to.id,
            content: payload.reply_to.content,
            messageTranslation: payload.reply_to.chat_message_translations.find(
              (t) => t.language === userLanguage,
            )?.content,
            author: {
              id: payload.reply_to.created_by.id,
              username: payload.reply_to.created_by.username,
              avatar: payload.reply_to.created_by.avatar ?? undefined,
              countryIso: payload.reply_to.created_by.country_iso,
              hasPaidMembership:
                payload.reply_to.created_by.has_paid_membership,
            },
          }
        : undefined,
      author: {
        id: payload.created_by.id,
        username: payload.created_by.username,
        avatar: payload.created_by.avatar ?? undefined,
        countryIso: payload.created_by.country_iso,
        hasPaidMembership: payload.created_by.has_paid_membership,
      },
    },
    likes: {
      isLiked: false,
      likes: 0,
    },
  };
}

export function useChatStorage() {
  const userStore = useUserStore();
  const { getModerationConfirmation } = useTextModerationConfirmation();
  const cursor = ref<string | null>(null);
  async function fetchMessagesForRoom(
    chatRoomId: string,
    limit: number = CHAT_MESSAGES_PAGE_SIZE,
  ): Promise<IChatMessageWithLikes[] | null> {
    const result = await apolloClient.query({
      query: RoomChatMessagesDocument,
      variables: {
        chat_room_id: chatRoomId,
        limit,
        cursor: cursor.value,
      },
      fetchPolicy: "no-cache",
    });

    const messagesData = result.data.chatMessagesForRoom ?? null;
    if (!messagesData) return null;

    const formattedMessages = messagesData.map((messageData) =>
      formatChatMessage(messageData),
    );

    cursor.value = formattedMessages.at(-1)?.message.id ?? null;

    return formattedMessages;
  }

  async function fetchMessage(
    id: string,
  ): Promise<IChatMessageWithLikes | null> {
    const result = await apolloClient.query({
      query: ChatMessageDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    const messageData = result.data.chatMessage ?? null;
    if (!messageData) return null;

    return formatChatMessage(messageData);
  }

  async function setLikeStatus(id: string, liked: boolean): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: LikeChatMessageDocument,
      variables: {
        chat_message_id: id,
        liked,
      },
      fetchPolicy: "no-cache",
    });

    return typeof result.data?.likeChatMessage?.chat_message_id === "string";
  }

  async function setMessagePinnedState(
    id: string,
    value: boolean,
  ): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: SetMessagePinnedStateDocumment,
      variables: {
        id,
        value,
      },
      fetchPolicy: "no-cache",
    });
    return (
      typeof result.data?.setMessagePinnedState === "boolean" &&
      result.data.setMessagePinnedState
    );
  }

  async function removeMessage(id: string): Promise<boolean> {
    const result = await apolloClient.mutate({
      mutation: DeleteChatMessageDocument,
      variables: {
        id,
      },
      fetchPolicy: "no-cache",
    });

    return result.data?.deleteChatMessage === true;
  }

  async function reportMessage(
    chatMessageId: string,
    reason?: string,
  ): Promise<
    | { type: "success"; reportId: string }
    | { type: "duplicate" }
    | { type: "self_report" }
    | { type: "error" }
  > {
    try {
      const result = await apolloClient.mutate({
        mutation: ReportChatMessageDocument,
        variables: {
          chat_message_id: chatMessageId,
          reason: reason || undefined,
        },
        fetchPolicy: "no-cache",
      });

      const data = result.data?.reportChatMessage;
      if (!data) return { type: "error" };

      if ("report_id" in data) {
        return { type: "success", reportId: data.report_id };
      }

      if ("error" in data) {
        if (data.error === "DuplicateReportError") return { type: "duplicate" };
        if (data.error === "SelfReportError") return { type: "self_report" };
      }

      return { type: "error" };
    } catch {
      return { type: "error" };
    }
  }

  async function persistMessage({
    message,
    isQuestion,
    chatRoomId,
    replyToId,
  }: {
    message: string;
    isQuestion: boolean;
    chatRoomId: string;
    replyToId?: string;
  }): Promise<IChatMessageWithLikes | false> {
    const confirmationToken = await getModerationConfirmation(message);

    if (!confirmationToken) return false;

    const result = await apolloClient.mutate({
      mutation: AddChatMessageDocument,
      variables: {
        content: message,
        is_question: isQuestion,
        chat_room_id: chatRoomId,
        moderation_confirmation_token: confirmationToken,
        reply_to_id: replyToId,
      },
      fetchPolicy: "no-cache",
    });

    const messageData = result.data?.addChatMessage ?? null;
    if (!messageData || messageData.__typename !== "ChatMessage") return false;

    return formatChatMessage(messageData);
  }

  function formatChatMessage(
    messageFragment:
      | FragmentType<typeof ChatMessageForServiceFragment>
      | FragmentType<typeof ChatMessageWithLikesForServiceFragment>,
  ): IChatMessageWithLikes {
    const messageData = getFragmentData(
      ChatMessageForServiceFragment,
      messageFragment as FragmentType<typeof ChatMessageForServiceFragment>,
    );

    let messageLikesData: DocumentType<
      typeof ChatMessageWithLikesForServiceFragment
    > | null = null;

    if ("likes" in messageFragment) {
      messageLikesData = getFragmentData(
        ChatMessageWithLikesForServiceFragment,
        messageFragment as FragmentType<
          typeof ChatMessageWithLikesForServiceFragment
        >,
      );
    }
    return {
      message: {
        id: messageData.id,
        content: messageData.content,
        messageTranslation: messageData.chat_message_translations.find(
          (t) => t.language === userStore.userLanguage,
        )?.content,
        isVisible:
          messageData.has_been_checked ||
          messageData.created_by_id === userStore.userId,
        isVerified: messageData.has_been_checked,
        isQuestion: messageData.is_question,
        isPinned: messageData.is_pinned,
        moderationStatus: messageData.moderation_status ?? null,
        timestamp: messageData.created_at,
        chatRoomId: messageData.chat_room_id,
        replyTo: messageData.reply_to
          ? {
              id: messageData.reply_to.id,
              content: messageData.reply_to.content,
              messageTranslation:
                messageData.reply_to.chat_message_translations.find(
                  (t) => t.language === userStore.userLanguage,
                )?.content,
              author: {
                id: messageData.reply_to.created_by.id,
                username: messageData.reply_to.created_by.username,
                avatar: messageData.reply_to.created_by.avatar ?? undefined,
                countryIso: messageData.reply_to.created_by.country_iso,
                hasPaidMembership:
                  messageData.reply_to.created_by.user_channel_memberships?.some(
                    (cm) => cm.channel_membership?.permission_mask > 0,
                  ),
              },
            }
          : undefined,
        author: {
          id: messageData.created_by.id,
          avatar: messageData.created_by.avatar ?? undefined,
          username: messageData.created_by.username,
          countryIso: messageData.created_by.country_iso,
          hasPaidMembership:
            messageData.created_by.user_channel_memberships?.some(
              (cm) => cm.channel_membership?.permission_mask > 0,
            ),
        },
      },
      likes: {
        isLiked: messageLikesData?.has_liked ?? false,
        likes: messageLikesData?.likes ?? 0,
      },
    };
  }

  return {
    fetchMessagesForRoom,
    fetchMessage,
    setLikeStatus,
    removeMessage,
    setMessagePinnedState,
    reportMessage,
    persistMessage,
    formatChatMessage,
  };
}
